import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp, 
  db 
} from '../../utils/database/firebase-config';

export const odometerService = {
  // Add new odometer reading
  async addReading(companyId, vehicleId, reading) {
    try {
      const readingsRef = collection(db, 'odometer_readings');
      const docRef = await addDoc(readingsRef, {
        companyId,
        vehicleId,
        reading: parseFloat(reading.reading),
        source: reading.source || 'manual', // 'manual', 'fuel_import', 'maintenance'
        sourceId: reading.sourceId || null, // Reference to fuel transaction, maintenance record, etc.
        notes: reading.notes || null,
        recordedBy: reading.recordedBy,
        recordedAt: reading.recordedAt || serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update vehicle's current odometer reading
      await this.updateVehicleOdometer(vehicleId, reading.reading);

      return docRef.id;
    } catch (error) {
      console.error('Error adding odometer reading:', error);
      throw error;
    }
  },

  // Get odometer readings for a vehicle
  async getVehicleReadings(companyId, vehicleId, limitCount = 50) {
    try {
      const readingsRef = collection(db, 'odometer_readings');
      // Remove orderBy to avoid composite index requirement
      const q = query(
        readingsRef,
        where('companyId', '==', companyId),
        where('vehicleId', '==', vehicleId)
      );
      
      const snapshot = await getDocs(q);
      let readings = snapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data,
          // Normalize recordedAt to always be a JS Date
          recordedAt: data.recordedAt?.toDate ? data.recordedAt.toDate() : new Date(data.recordedAt)
        };
      });
      
      // Sort in memory and apply limit
      readings.sort((a, b) => {
        const aTime = a.recordedAt instanceof Date ? a.recordedAt : new Date(a.recordedAt);
        const bTime = b.recordedAt instanceof Date ? b.recordedAt : new Date(b.recordedAt);
        return bTime - aTime; // desc order
      });
      
      return readings.slice(0, limitCount);
    } catch (error) {
      console.error('Error getting vehicle readings:', error);
      throw error;
    }
  },

  // Get latest odometer reading for a vehicle
  async getLatestReading(companyId, vehicleId) {
    try {
      const readingsRef = collection(db, 'odometer_readings');
      // Remove orderBy to avoid composite index requirement
      const q = query(
        readingsRef,
        where('companyId', '==', companyId),
        where('vehicleId', '==', vehicleId)
      );
      
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      
      // Get all readings and find the latest one in memory
      let latestReading = null;
      let latestTime = null;
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const recordedAt = data.recordedAt?.toDate ? data.recordedAt.toDate() : new Date(data.recordedAt);
        
        if (!latestTime || recordedAt > latestTime) {
          latestTime = recordedAt;
          latestReading = { 
            id: doc.id, 
            ...data,
            recordedAt
          };
        }
      });
      
      return latestReading;
    } catch (error) {
      console.error('Error getting latest reading:', error);
      throw error;
    }
  },

  // Get last odometer reading for a vehicle (alias for getLatestReading)
  async getLastReading(companyId, vehicleId) {
    return this.getLatestReading(companyId, vehicleId);
  },

  // Find odometer reading by source
  async findReadingBySource(companyId, vehicleId, source, sourceId) {
    try {
      // Use the getVehicleReadings method which already handles the composite index issue
      const readings = await this.getVehicleReadings(companyId, vehicleId, 1000);
      
      // Find the reading with matching source and sourceId
      const matchingReading = readings.find(reading => 
        reading.source === source && reading.sourceId === sourceId
      );
      
      return matchingReading || null;
    } catch (error) {
      console.error('Error finding reading by source:', error);
      throw error;
    }
  },

  // Update vehicle's current odometer reading
  async updateVehicleOdometer(vehicleId, reading) {
    try {
      const vehicleRef = doc(db, 'vehicles', vehicleId);
      await updateDoc(vehicleRef, {
        currentOdometer: parseFloat(reading),
        odometerUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating vehicle odometer:', error);
      throw error;
    }
  },

  // Get odometer statistics for a vehicle
  async getVehicleOdometerStats(companyId, vehicleId) {
    try {
      const readings = await this.getVehicleReadings(companyId, vehicleId, 100);
      
      if (readings.length === 0) {
        return {
          totalReadings: 0,
          currentReading: null,
          totalDistance: 0,
          averageDistance: 0,
          lastUpdated: null
        };
      }

      const sortedReadings = readings.sort((a, b) => {
        const aDate = a.recordedAt instanceof Date ? a.recordedAt : new Date(a.recordedAt);
        const bDate = b.recordedAt instanceof Date ? b.recordedAt : new Date(b.recordedAt);
        return aDate - bDate;
      });

      const firstReading = sortedReadings[0];
      const lastReading = sortedReadings[sortedReadings.length - 1];
      const totalDistance = lastReading.reading - firstReading.reading;

      // Calculate distances between consecutive readings
      const distances = [];
      for (let i = 1; i < sortedReadings.length; i++) {
        const distance = sortedReadings[i].reading - sortedReadings[i - 1].reading;
        if (distance > 0) distances.push(distance);
      }

      const averageDistance = distances.length > 0 
        ? distances.reduce((sum, dist) => sum + dist, 0) / distances.length 
        : 0;

      return {
        totalReadings: readings.length,
        currentReading: lastReading.reading,
        totalDistance: Math.max(0, totalDistance),
        averageDistance: Math.round(averageDistance),
        lastUpdated: lastReading.recordedAt,
        firstReading: firstReading.reading,
        lastReading: lastReading.reading
      };
    } catch (error) {
      console.error('Error getting odometer stats:', error);
      throw error;
    }
  },

  // Import odometer readings from fuel transactions
  async importFromFuelTransactions(companyId, vehicleId) {
    try {
      // This would be called when processing fuel imports
      // to automatically create odometer readings from fuel data
      const fuelTransactionsRef = collection(db, 'fuel_transactions');
      const q = query(
        fuelTransactionsRef,
        where('companyId', '==', companyId),
        where('vehicle_plate', '==', vehicleId), // This might need adjustment based on how vehicle matching works
        where('odometer', '>', 0),
        orderBy('fill_time', 'asc')
      );

      const snapshot = await getDocs(q);
      const importedReadings = [];

      for (const fuelDoc of snapshot.docs) {
        const fuelData = fuelDoc.data();
        
        // Check if we already have an odometer reading for this fuel transaction
        const existingReadingQuery = query(
          collection(db, 'odometer_readings'),
          where('companyId', '==', companyId),
          where('vehicleId', '==', vehicleId),
          where('sourceId', '==', fuelDoc.id)
        );
        
        const existingSnapshot = await getDocs(existingReadingQuery);
        
        if (existingSnapshot.empty && fuelData.odometer) {
          const readingId = await this.addReading(companyId, vehicleId, {
            reading: fuelData.odometer,
            source: 'fuel_import',
            sourceId: fuelDoc.id,
            notes: `Imported from fuel transaction at ${fuelData.station_name || 'Unknown station'}`,
            recordedBy: fuelData.importedBy || 'system',
            recordedAt: fuelData.fill_time?.toDate?.() || new Date(fuelData.fill_time)
          });
          
          importedReadings.push(readingId);
        }
      }

      return importedReadings;
    } catch (error) {
      console.error('Error importing odometer readings from fuel transactions:', error);
      throw error;
    }
  },

  // Validate odometer reading
  validateReading(newReading, lastReading = null) {
    const errors = [];
    
    if (!newReading || newReading <= 0) {
      errors.push('Odometer reading must be a positive number');
    }
    
    if (lastReading && newReading < lastReading) {
      errors.push(`New reading (${newReading}) cannot be less than last reading (${lastReading})`);
    }
    
    // Check for unrealistic jumps (more than 10,000 km in one reading)
    if (lastReading && (newReading - lastReading) > 10000) {
      errors.push(`Reading increase of ${newReading - lastReading} km seems unusually high`);
    }
    
    return errors;
  }
};